import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Comment01Icon as MessageSquare, Cancel01Icon as X, SentIcon as Send, Bug01Icon as Bug, Layers01Icon as Layers, Doc01Icon as ScrollText, StarIcon as Star, ThumbsUpIcon as ThumbsUp, Add01Icon as Plus, Tick01Icon as CheckCircle2, SparklesIcon as Sparkles, FlashIcon as Zap, Shield01Icon as ShieldCheck, Wrench01Icon as Wrench, Idea01Icon as Lightbulb, Tick01Icon as Check, MusicNote01Icon as Music, TextIcon as Type } from 'hugeicons-react'

export type FeedbackTabType = 'feedback' | 'roadmap' | 'changelog'

interface RoadmapItem {
    id: string
    title: string
    description: string
    column: 'requested' | 'in-progress' | 'completed'
    votes: number
    threshold?: number
    hasVoted?: boolean
}

const INITIAL_ROADMAP_ITEMS: RoadmapItem[] = [
    // 💡 Ideas / Requested
    {
        id: '1',
        title: 'Auto Captions & Speech-to-Text',
        description: 'Local in-browser speech-to-text for private automatic caption generation on screen recordings.',
        column: 'requested',
        votes: 7,
        threshold: 10
    },
    {
        id: '2',
        title: 'Undo / Redo Support',
        description: 'Full history support for undoing and redoing actions in the video timeline editor.',
        column: 'completed',
        votes: 5,
        threshold: 10
    },
    {
        id: '3',
        title: 'Background Blur & Camera Mask Shapes',
        description: 'Circular camera overlay shapes and background blur filters for camera recordings.',
        column: 'requested',
        votes: 8,
        threshold: 10
    },
    {
        id: '4',
        title: 'Text Slides & Banner Overlays',
        description: 'Add text slides and intro/outro banners between video clips with customizable styles.',
        column: 'requested',
        votes: 4,
        threshold: 10
    },


    // 🛠️ In Progress
    {
        id: '5',
        title: 'Auto-Zoom on Cursor Clicks',
        description: 'Smoothly zoom in on key elements when mouse click events are detected in the timeline.',
        column: 'in-progress',
        votes: 14,
        threshold: 10
    },
    {
        id: '8',
        title: 'Web App Cursor Overlay',
        description: 'Custom cursor shapes (Circle, Ring, Arrow, Hand), size scale up to 3x, and click ripple effects.',
        column: 'in-progress',
        votes: 24,
        threshold: 10
    },
    {
        id: '7',
        title: 'Audio Noise Suppression',
        description: 'Eliminate microphone hum and background noise for crystal clear voiceovers.',
        column: 'in-progress',
        votes: 12,
        threshold: 10
    },

    // 🟢 Done / Completed
    {
        id: '6',
        title: 'Split & Trim Video Clips',
        description: 'Cut out unwanted segments from the middle of screen recordings with multi-split points.',
        column: 'completed',
        votes: 18,
        threshold: 10
    },
    {
        id: '9',
        title: 'System Audio Capture & Tips',
        description: 'Capture system/tab audio with helpful setup modal instructions.',
        column: 'completed',
        votes: 31,
        threshold: 10
    },
    {
        id: '10',
        title: 'Custom Background Uploads & Gradients',
        description: 'Upload custom image backgrounds or choose curated 4K gradient presets.',
        column: 'completed',
        votes: 28,
        threshold: 10
    }
]

const CHANGELOG_ENTRIES = [
    {
        version: 'v1.4.0',
        date: 'August 2026',
        tag: 'Latest Release',
        items: [
            { icon: Music, title: 'Sound Effects & Volume Sliders', desc: 'Introduced 13 built-in catalog sound effects, local file uploads for music/SFX, and volume slider controls for individual audio layers.' },
            { icon: Wrench, title: 'Timeline & Audio Constraint Refinement', desc: 'Constrained audio dragging handles to prevent resizing beyond actual audio limits, and shifted timeline time frame coordinates leftward for better alignment.' },
            { icon: ShieldCheck, title: 'Sleek Setup Dropdowns & Mic Controls', desc: 'Replaced standard native selector overlays with glassmorphic custom dropdown menus, fixed setup switch knob translate offsets, and enabled instant recorder auto-restart on Retake.' },
            { icon: Zap, title: 'Live Pause / Resume Recording', desc: 'Added pause and resume capabilities to the active screen recorder floating overlay and the Picture-in-Picture preview window.' }
        ]
    },
    {
        version: 'v1.3.0',
        date: 'July 2026',
        tag: 'Feature Update',
        items: [
            { icon: Music, title: 'Royalty-Free Audio Library', desc: 'Browse, search, and preview CC0-licensed music tracks categorized into Focus, Chill, Gaming, Corporate, and Ambient vibes.' },
            { icon: Sparkles, title: 'Dynamic Waveform & Timeline Importer', desc: 'On-demand audio decoding using AudioContext to dynamically display track waveforms and place background music tracks on the timeline.' },
            { icon: ScrollText, title: 'Interactive Subtitle Builder', desc: 'Redesigned workspace with inline active subtitle editors, quick duration setters, content reordering arrows, timing gap formatting, and SRT/VTT file & paste-to-timing parsing.' },
            { icon: Type, title: 'Transitions & Styling Customizations', desc: 'Advanced entrance/exit animations matching exact MP4 exports, outline stroke bug fixes, global design sync, and automatically collapsible panel focuses.' }
        ]
    },
    {
        version: 'v1.2.0',
        date: 'July 2026',
        tag: 'Feature Update',
        items: [
            { icon: Sparkles, title: 'Web App Cursor Overlay', desc: 'Custom cursor shapes, enlarged size scale, highlight colors, and click ripple effects now active on web app recordings.' },
            { icon: Zap, title: 'Recording Setup & System Audio Tip', desc: 'Default microphone state set to muted by default, added step guidance, and clear instructions for capturing system/tab audio.' },
            { icon: Sparkles, title: 'Animated Monitor Recorder Icon', desc: 'Integrated Iconsax bold monitor-recorder vector icon with elastic spring bezier curve physics.' },
            { icon: ShieldCheck, title: 'Stock Photos Fix', desc: 'Resolved category key mappings and added high-res curated fallback photos across all categories.' }
        ]
    },
    {
        version: 'v1.1.0',
        date: 'June 2026',
        tag: 'Feature Update',
        items: [
            { icon: Layers, title: 'Area Selection & Desktop Integration', desc: 'Select specific rectangular regions of your screen to record with the Electron desktop app.' },
            { icon: ScrollText, title: 'Alpha Releases Page', desc: 'Live release downloads page with direct GitHub releases integration and build status.' }
        ]
    },
    {
        version: 'v1.0.0',
        date: 'May 2026',
        tag: 'Initial Launch',
        items: [
            { icon: Zap, title: 'ScreenRecord.ing Launch', desc: '100% in-browser screen recording studio with high-res MP4/WebM export, canvas padding, and gradient background presets.' }
        ]
    }
]

interface FeedbackModalProps {
    isOpen: boolean
    initialTab?: FeedbackTabType
    onClose: () => void
}

export default function FeedbackModal({ isOpen, initialTab = 'feedback', onClose }: FeedbackModalProps) {
    const [activeTab, setActiveTab] = useState<FeedbackTabType>(initialTab)

    // Form states
    const [rating, setRating] = useState<number>(5)
    const [feedbackCategory, setFeedbackCategory] = useState('General')
    const [feedbackMessage, setFeedbackMessage] = useState('')
    const [feedbackEmail, setFeedbackEmail] = useState('')
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)

    const [bugTitle, setBugTitle] = useState('')
    const [bugSteps, setBugSteps] = useState('')
    const [bugEmail, setBugEmail] = useState('')
    const [bugSubmitted, setBugSubmitted] = useState(false)

    const [isSubmitting, setIsSubmitting] = useState(false)

    // Roadmap state
    const [roadmapItems, setRoadmapItems] = useState<RoadmapItem[]>(() => {
        const savedVotes = localStorage.getItem('sr_voted_features_v2')
        const votedIds: string[] = savedVotes ? JSON.parse(savedVotes) : []
        return INITIAL_ROADMAP_ITEMS.map(item => ({
            ...item,
            hasVoted: votedIds.includes(item.id),
            votes: votedIds.includes(item.id) ? item.votes + 1 : item.votes
        }))
    })

    const [showNewFeatureModal, setShowNewFeatureModal] = useState(false)
    const [newTitle, setNewTitle] = useState('')
    const [newDesc, setNewDesc] = useState('')

    useEffect(() => {
        if (isOpen) {
            setActiveTab(initialTab)
            setFeedbackSubmitted(false)
            setBugSubmitted(false)
        }
    }, [isOpen, initialTab])

    if (!isOpen) return null

    // Upvote handler
    const toggleVote = (id: string) => {
        const votedIds: string[] = JSON.parse(localStorage.getItem('sr_voted_features_v2') || '[]')
        const isVoted = votedIds.includes(id)

        const newVotedIds = isVoted ? votedIds.filter(v => v !== id) : [...votedIds, id]
        localStorage.setItem('sr_voted_features_v2', JSON.stringify(newVotedIds))

        setRoadmapItems(prev =>
            prev.map(item => {
                if (item.id === id) {
                    return {
                        ...item,
                        hasVoted: !isVoted,
                        votes: isVoted ? item.votes - 1 : item.votes + 1
                    }
                }
                return item
            })
        )
    }

    const handleAddFeature = (e: React.FormEvent) => {
        e.preventDefault()
        if (!newTitle.trim()) return

        const newItem: RoadmapItem = {
            id: Date.now().toString(),
            title: newTitle,
            description: newDesc,
            column: 'requested',
            votes: 1,
            threshold: 10,
            hasVoted: true
        }

        setRoadmapItems([newItem, ...roadmapItems])
        setNewTitle('')
        setNewDesc('')
        setShowNewFeatureModal(false)
    }

    const handleSubmitFeedback = (e: React.FormEvent) => {
        e.preventDefault()
        setIsSubmitting(true)
        setTimeout(() => {
            setIsSubmitting(false)
            setFeedbackSubmitted(true)
            setFeedbackMessage('')
        }, 500)
    }

    const handleSubmitBug = (e: React.FormEvent) => {
        e.preventDefault()
        setIsSubmitting(true)
        setTimeout(() => {
            setIsSubmitting(false)
            setBugSubmitted(true)
            setBugTitle('')
            setBugSteps('')
        }, 500)
    }

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/80 backdrop-blur-md">
                {/* Backdrop Click */}
                <div className="absolute inset-0" onClick={onClose} />

                {/* Bottom to Top Extended Drawer (No Bottom Cutoff) */}
                <motion.div
                    initial={{ y: '100%', opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: '100%', opacity: 0 }}
                    transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                    className="relative z-10 w-full max-w-6xl h-[92vh] mb-[-2px] bg-[#0A0A0C] border-t border-x border-white/10 rounded-t-[32px] shadow-2xl overflow-hidden flex flex-col"
                >
                    {/* Top Handle for mobile */}
                    <div className="w-12 h-1.5 bg-white/20 rounded-full mx-auto mt-3 mb-1 sm:hidden shrink-0" />

                    {/* Header & Tabs */}
                    <div className="p-6 pb-4 border-b border-white/5 flex flex-col md:flex-row md:items-center justify-between gap-4 shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-brand-500/10 border border-brand-500/20 text-brand-400 rounded-2xl">
                                {activeTab === 'feedback' && <MessageSquare size={20} />}
                                {activeTab === 'roadmap' && <Layers size={20} />}
                                {activeTab === 'changelog' && <ScrollText size={20} />}
                            </div>
                            <div>
                                <h3 className="text-xl font-display font-bold text-white">
                                    {activeTab === 'feedback' && 'Feedback & Bug Reports'}
                                    {activeTab === 'roadmap' && 'Feature Roadmap'}
                                    {activeTab === 'changelog' && 'Changelog'}
                                </h3>
                                <p className="text-xs text-txt-dim">
                                    {activeTab === 'feedback' && 'Share your experience or report bugs directly to our team.'}
                                    {activeTab === 'roadmap' && 'Vote on requested features and track what we are building.'}
                                    {activeTab === 'changelog' && 'Explore the latest updates and release history.'}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            {/* Tab Bar */}
                            <div className="flex items-center gap-1 p-1 bg-surface-900 rounded-2xl border border-white/5">
                                <button
                                    onClick={() => setActiveTab('feedback')}
                                    className={`py-2 px-3.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all ${
                                        activeTab === 'feedback'
                                            ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20'
                                            : 'text-txt-dim hover:text-white hover:bg-white/5'
                                    }`}
                                >
                                    <MessageSquare size={14} />
                                    <span>Feedback & Bugs</span>
                                </button>
                                <button
                                    onClick={() => setActiveTab('roadmap')}
                                    className={`py-2 px-3.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all ${
                                        activeTab === 'roadmap'
                                            ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20'
                                            : 'text-txt-dim hover:text-white hover:bg-white/5'
                                    }`}
                                >
                                    <Layers size={14} />
                                    <span>Roadmap</span>
                                </button>
                                <button
                                    onClick={() => setActiveTab('changelog')}
                                    className={`py-2 px-3.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all ${
                                        activeTab === 'changelog'
                                            ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20'
                                            : 'text-txt-dim hover:text-white hover:bg-white/5'
                                    }`}
                                >
                                    <ScrollText size={14} />
                                    <span>Changelog</span>
                                </button>
                            </div>

                            <button
                                onClick={onClose}
                                className="p-2 text-txt-dim hover:text-white hover:bg-white/10 rounded-full transition-colors shrink-0"
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Drawer Content Area */}
                    <div className="p-6 overflow-y-auto flex-1 scrollbar-none">
                        {/* 💬 TAB 1: SPLIT 2-COLUMN VIEW (FEEDBACK LEFT, BUG REPORT RIGHT) */}
                        {activeTab === 'feedback' && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full">
                                {/* Left Side: General Feedback */}
                                <div className="p-6 bg-surface-900/50 border border-white/5 rounded-3xl space-y-4 flex flex-col">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="p-2 bg-brand-500/10 text-brand-400 rounded-xl">
                                            <MessageSquare size={18} />
                                        </div>
                                        <div>
                                            <h4 className="text-base font-bold text-white">General Feedback</h4>
                                            <p className="text-xs text-txt-dim">Tell us about your experience or suggestions</p>
                                        </div>
                                    </div>

                                    {feedbackSubmitted ? (
                                        <div className="my-auto text-center py-10 space-y-3">
                                            <div className="w-12 h-12 rounded-full bg-success-500/20 text-success-400 flex items-center justify-center mx-auto border border-success-500/30">
                                                <CheckCircle2 size={24} />
                                            </div>
                                            <h5 className="text-base font-bold text-white">Thank you for your feedback!</h5>
                                            <p className="text-xs text-txt-dim">Your ideas directly influence our next release.</p>
                                            <button
                                                onClick={() => setFeedbackSubmitted(false)}
                                                className="mt-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-xs font-semibold text-white rounded-xl transition-colors"
                                            >
                                                Send Another Feedback
                                            </button>
                                        </div>
                                    ) : (
                                        <form onSubmit={handleSubmitFeedback} className="space-y-4 flex-1 flex flex-col justify-between">
                                            <div className="space-y-4">
                                                {/* Rating */}
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-semibold text-white">Overall Rating</label>
                                                    <div className="flex items-center gap-2">
                                                        {[1, 2, 3, 4, 5].map((star) => (
                                                            <button
                                                                type="button"
                                                                key={star}
                                                                onClick={() => setRating(star)}
                                                                className="p-1 hover:scale-110 transition-transform"
                                                            >
                                                                <Star
                                                                    size={22}
                                                                    className={star <= rating ? 'text-amber-400 fill-amber-400' : 'text-white/20'}
                                                                />
                                                            </button>
                                                        ))}
                                                        <span className="text-xs text-txt-dim ml-2 font-medium">({rating}/5)</span>
                                                    </div>
                                                </div>

                                                {/* Category */}
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-semibold text-white">Category</label>
                                                    <select
                                                        value={feedbackCategory}
                                                        onChange={(e) => setFeedbackCategory(e.target.value)}
                                                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-xs text-white outline-none focus:border-brand-500 transition-colors"
                                                    >
                                                        <option value="General">General Experience</option>
                                                        <option value="UX/UI">Interface & Design</option>
                                                        <option value="Feature">Feature Request</option>
                                                        <option value="Export">Video Export & Quality</option>
                                                    </select>
                                                </div>

                                                {/* Message */}
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-semibold text-white">Your Message</label>
                                                    <textarea
                                                        required
                                                        rows={4}
                                                        placeholder="What do you think of ScreenRecord.ing?"
                                                        value={feedbackMessage}
                                                        onChange={(e) => setFeedbackMessage(e.target.value)}
                                                        className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white placeholder:text-txt-dim outline-none focus:border-brand-500 transition-colors resize-none"
                                                    />
                                                </div>

                                                {/* Email */}
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-semibold text-white">Email (Optional)</label>
                                                    <input
                                                        type="email"
                                                        placeholder="your@email.com"
                                                        value={feedbackEmail}
                                                        onChange={(e) => setFeedbackEmail(e.target.value)}
                                                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-xs text-white placeholder:text-txt-dim outline-none focus:border-brand-500 transition-colors"
                                                    />
                                                </div>
                                            </div>

                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="w-full mt-4 py-3 bg-brand-500 hover:bg-brand-400 text-white rounded-xl font-bold text-xs shadow-lg shadow-brand-500/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                                            >
                                                <Send size={14} />
                                                {isSubmitting ? 'Sending...' : 'Submit Feedback'}
                                            </button>
                                        </form>
                                    )}
                                </div>

                                {/* Right Side: Bug Report */}
                                <div className="p-6 bg-surface-900/50 border border-white/5 rounded-3xl space-y-4 flex flex-col">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="p-2 bg-danger-500/10 text-danger-400 rounded-xl">
                                            <Bug size={18} />
                                        </div>
                                        <div>
                                            <h4 className="text-base font-bold text-white">Report a Bug</h4>
                                            <p className="text-xs text-txt-dim">Found an issue? Let us know so we can fix it</p>
                                        </div>
                                    </div>

                                    {bugSubmitted ? (
                                        <div className="my-auto text-center py-10 space-y-3">
                                            <div className="w-12 h-12 rounded-full bg-brand-500/20 text-brand-400 flex items-center justify-center mx-auto border border-brand-500/30">
                                                <CheckCircle2 size={24} />
                                            </div>
                                            <h5 className="text-base font-bold text-white">Bug Report Received!</h5>
                                            <p className="text-xs text-txt-dim">Our team is on it!</p>
                                            <button
                                                onClick={() => setBugSubmitted(false)}
                                                className="mt-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-xs font-semibold text-white rounded-xl transition-colors"
                                            >
                                                Submit Another Bug Report
                                            </button>
                                        </div>
                                    ) : (
                                        <form onSubmit={handleSubmitBug} className="space-y-4 flex-1 flex flex-col justify-between">
                                            <div className="space-y-4">
                                                <div className="p-3 bg-danger-500/5 border border-danger-500/10 rounded-xl text-xs text-danger-300 flex items-center gap-2">
                                                    <Bug size={14} className="shrink-0 text-danger-400" />
                                                    <span>Help us fix issues faster by describing what happened.</span>
                                                </div>

                                                {/* Issue Summary */}
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-semibold text-white">Bug Summary</label>
                                                    <input
                                                        type="text"
                                                        required
                                                        placeholder="e.g., Audio cut off at 2 minutes"
                                                        value={bugTitle}
                                                        onChange={(e) => setBugTitle(e.target.value)}
                                                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-xs text-white placeholder:text-txt-dim outline-none focus:border-brand-500 transition-colors"
                                                    />
                                                </div>

                                                {/* Steps to Reproduce */}
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-semibold text-white">Steps to Reproduce</label>
                                                    <textarea
                                                        required
                                                        rows={4}
                                                        placeholder="1. Start recording screen&#10;2. Click stop recording...&#10;3. Notice error..."
                                                        value={bugSteps}
                                                        onChange={(e) => setBugSteps(e.target.value)}
                                                        className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white placeholder:text-txt-dim outline-none focus:border-brand-500 transition-colors resize-none"
                                                    />
                                                </div>

                                                {/* Email */}
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-semibold text-white">Your Email</label>
                                                    <input
                                                        type="email"
                                                        placeholder="your@email.com"
                                                        value={bugEmail}
                                                        onChange={(e) => setBugEmail(e.target.value)}
                                                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-xs text-white placeholder:text-txt-dim outline-none focus:border-brand-500 transition-colors"
                                                    />
                                                </div>
                                            </div>

                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="w-full mt-4 py-3 bg-danger-500 hover:bg-danger-400 text-white rounded-xl font-bold text-xs shadow-lg shadow-danger-500/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                                            >
                                                <Bug size={14} />
                                                {isSubmitting ? 'Sending...' : 'Report Bug'}
                                            </button>
                                        </form>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* 🗺️ TAB 2: 3-COLUMN KANBAN ROADMAP (REQUESTED / IN PROGRESS / COMPLETED) */}
                        {activeTab === 'roadmap' && (
                            <div className="space-y-4 h-full flex flex-col">
                                <div className="flex items-center justify-between shrink-0">
                                    <p className="text-xs text-txt-dim">Vote on features or submit new ideas for upcoming releases:</p>
                                    <button
                                        onClick={() => setShowNewFeatureModal(true)}
                                        className="text-xs font-bold text-white bg-brand-500 hover:bg-brand-400 px-4 py-2 rounded-xl shadow-lg shadow-brand-500/20 transition-all flex items-center gap-1.5"
                                    >
                                        <Plus size={14} /> New Request
                                    </button>
                                </div>

                                {/* New Feature Request Form Modal */}
                                {showNewFeatureModal && (
                                    <form onSubmit={handleAddFeature} className="p-4 bg-surface-900 border border-brand-500/30 rounded-2xl space-y-3 animate-in fade-in duration-300">
                                        <h5 className="text-xs font-bold text-white">Suggest a Feature</h5>
                                        <input
                                            type="text"
                                            required
                                            placeholder="Feature Title"
                                            value={newTitle}
                                            onChange={(e) => setNewTitle(e.target.value)}
                                            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-xs text-white outline-none focus:border-brand-500"
                                        />
                                        <textarea
                                            rows={2}
                                            placeholder="Description of the feature..."
                                            value={newDesc}
                                            onChange={(e) => setNewDesc(e.target.value)}
                                            className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-brand-500 resize-none"
                                        />
                                        <div className="flex justify-end gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setShowNewFeatureModal(false)}
                                                className="px-3 py-1.5 text-xs text-txt-dim hover:text-white"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="px-4 py-1.5 bg-brand-500 text-white font-bold text-xs rounded-xl hover:bg-brand-400"
                                            >
                                                Submit Feature
                                            </button>
                                        </div>
                                    </form>
                                )}

                                {/* 3-Column Kanban Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 flex-1 items-start">
                                    {/* COLUMN 1: 💡 IDEAS / REQUESTED */}
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between pb-2 border-b border-white/5">
                                            <div className="flex items-center gap-2 text-xs font-bold text-amber-400 uppercase tracking-wider">
                                                <Lightbulb size={16} />
                                                <span>Ideas / Requested</span>
                                            </div>
                                            <span className="text-[10px] text-txt-dim font-medium">10 votes to proceed</span>
                                        </div>

                                        <div className="space-y-3">
                                            {roadmapItems.filter(i => i.column === 'requested').map((item) => (
                                                <div
                                                    key={item.id}
                                                    className="p-4 bg-white/[0.02] hover:bg-white/[0.04] border border-white/5 rounded-2xl space-y-3 transition-all"
                                                >
                                                    <div className="flex items-start justify-between gap-3">
                                                        <h5 className="text-xs font-bold text-white leading-snug">{item.title}</h5>
                                                        <button
                                                            onClick={() => toggleVote(item.id)}
                                                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border shrink-0 text-xs font-bold transition-all ${
                                                                item.hasVoted
                                                                    ? 'bg-brand-500 border-brand-500 text-white shadow-md shadow-brand-500/30'
                                                                    : 'bg-white/5 border-white/10 text-txt-dim hover:bg-white/10 hover:text-white'
                                                            }`}
                                                        >
                                                            <ThumbsUp size={12} className={item.hasVoted ? 'fill-white' : ''} />
                                                            <span className="tabular-nums">{item.votes}</span>
                                                        </button>
                                                    </div>

                                                    <p className="text-[11px] text-txt-dim leading-relaxed">{item.description}</p>

                                                    {/* Progress Bar */}
                                                    <div className="space-y-1 pt-1">
                                                        <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                                                            <div
                                                                className="h-full bg-brand-500 rounded-full transition-all duration-300"
                                                                style={{ width: `${Math.min(100, (item.votes / (item.threshold || 10)) * 100)}%` }}
                                                            />
                                                        </div>
                                                        <div className="text-[9px] text-txt-dim font-mono">
                                                            {item.votes}/{item.threshold || 10} votes needed
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* COLUMN 2: 🛠️ IN PROGRESS */}
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between pb-2 border-b border-white/5">
                                            <div className="flex items-center gap-2 text-xs font-bold text-brand-400 uppercase tracking-wider">
                                                <Wrench size={16} />
                                                <span>In Progress</span>
                                            </div>
                                            <span className="text-[10px] text-brand-400/80 font-medium">Building now</span>
                                        </div>

                                        <div className="space-y-3">
                                            {roadmapItems.filter(i => i.column === 'in-progress').map((item) => (
                                                <div
                                                    key={item.id}
                                                    className="p-4 bg-brand-500/[0.04] border border-brand-500/20 rounded-2xl space-y-3 transition-all"
                                                >
                                                    <div className="flex items-start justify-between gap-3">
                                                        <h5 className="text-xs font-bold text-white leading-snug">{item.title}</h5>
                                                        <button
                                                            onClick={() => toggleVote(item.id)}
                                                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border shrink-0 text-xs font-bold transition-all ${
                                                                item.hasVoted
                                                                    ? 'bg-brand-500 border-brand-500 text-white shadow-md shadow-brand-500/30'
                                                                    : 'bg-white/5 border-white/10 text-txt-dim hover:bg-white/10 hover:text-white'
                                                            }`}
                                                        >
                                                            <ThumbsUp size={12} className={item.hasVoted ? 'fill-white' : ''} />
                                                            <span className="tabular-nums">{item.votes}</span>
                                                        </button>
                                                    </div>

                                                    <p className="text-[11px] text-txt-dim leading-relaxed">{item.description}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* COLUMN 3: 🟢 DONE / COMPLETED */}
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between pb-2 border-b border-white/5">
                                            <div className="flex items-center gap-2 text-xs font-bold text-success-400 uppercase tracking-wider">
                                                <CheckCircle2 size={16} />
                                                <span>Done / Shipped</span>
                                            </div>
                                            <span className="text-[10px] text-success-400/80 font-medium">Live</span>
                                        </div>

                                        <div className="space-y-3">
                                            {roadmapItems.filter(i => i.column === 'completed').map((item) => (
                                                <div
                                                    key={item.id}
                                                    className="p-4 bg-white/[0.02] border border-white/5 rounded-2xl space-y-2 transition-all opacity-90"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <Check size={14} className="text-success-400 shrink-0" />
                                                        <h5 className="text-xs font-bold text-white leading-snug">{item.title}</h5>
                                                    </div>
                                                    <p className="text-[11px] text-txt-dim leading-relaxed pl-5">{item.description}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 📜 TAB 3: CHANGELOG */}
                        {activeTab === 'changelog' && (
                            <div className="space-y-6">
                                {CHANGELOG_ENTRIES.map((release, i) => (
                                    <div key={i} className="relative pl-6 border-l border-white/10 space-y-3">
                                        <div className="absolute -left-2 top-0 w-4 h-4 rounded-full bg-brand-500 border-4 border-[#1C1C1E]" />

                                        <div className="flex items-center gap-3">
                                            <h4 className="text-base font-bold text-white">{release.version}</h4>
                                            <span className="text-[10px] font-semibold text-brand-400 bg-brand-500/10 border border-brand-500/20 px-2.5 py-0.5 rounded-full">
                                                {release.tag}
                                            </span>
                                            <span className="text-xs text-txt-dim ml-auto">{release.date}</span>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {release.items.map((item, j) => {
                                                const ItemIcon = item.icon
                                                return (
                                                    <div key={j} className="p-3.5 bg-white/[0.02] border border-white/5 rounded-2xl flex items-start gap-3">
                                                        <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 shrink-0 mt-0.5">
                                                            <ItemIcon size={14} />
                                                        </div>
                                                        <div>
                                                            <h5 className="text-xs font-bold text-white mb-0.5">{item.title}</h5>
                                                            <p className="text-[11px] text-txt-dim leading-relaxed">{item.desc}</p>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    )
}
